import React, { useMemo, forwardRef, useRef, useImperativeHandle, Context } from 'react'
import classnames from 'classnames'
import { AmplitudeContext, useInputTypingEventTracking, ContextType } from '@platform/utils'

import { SearchIcon, CircleXFilledIcon } from '../../assets/icons/icons'
import IconButton from '../icon-button/icon-button'

import styles from './index.module.scss'

export type SearchBarBaseProps = {
  label: string
  id: string
  size?: 'small' | 'medium'
  searchButtonType?: 'submit' | 'button'
  variant?: 'live-search' | 'search-with-submit'
  disabled?: boolean
  hiddenLabel?: boolean
  onSearchClear: () => void
  onSearchSubmit?: () => void
  className?: string
  inputClassName?: string
  trackingIdentifier?: string
  shouldTrackTerms?: boolean
}

export type BaseSearchBarProps = SearchBarBaseProps

export type SearchBarProps = BaseSearchBarProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof BaseSearchBarProps>

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      label,
      id,
      searchButtonType = 'submit',
      size = 'medium',
      variant = 'live-search',
      onSearchClear,
      onSearchSubmit,
      className = '',
      inputClassName = '',
      disabled = false,
      hiddenLabel = false,
      trackingIdentifier,
      shouldTrackTerms = false,
      ...rest
    },
    ref
  ) => {
    const innerRef = useRef<HTMLInputElement | null>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

    useInputTypingEventTracking(
      innerRef,
      AmplitudeContext as unknown as Context<ContextType>,
      'input_change',
      {
        id: trackingIdentifier,
        label,
      },
      shouldTrackTerms
    )

    const inputClasses = classnames(
      styles['input'],
      styles['button-input-icons'],
      {
        [styles['has-start-adornment']]: variant === 'live-search',
      },
      inputClassName
    )

    const renderSearchButton = useMemo(() => {
      return (
        variant === 'search-with-submit' && (
          <IconButton
            disabled={disabled}
            className={styles['search-button']}
            onClick={onSearchSubmit}
            type={searchButtonType}
            size={size}
            variant="default"
            icon={SearchIcon}
            label="Search"
            trackingIdentifier="Search"
          />
        )
      )
    }, [disabled, size, variant, onSearchSubmit, searchButtonType])

    const renderClearButton = useMemo(() => {
      return (
        <IconButton
          disabled={disabled}
          className={classnames(styles['clear-button'], styles[size], {
            [styles['without-search-button']]: variant !== 'search-with-submit',
          })}
          onClick={onSearchClear}
          type="button"
          size={size}
          variant="ghost"
          icon={CircleXFilledIcon}
          label="Clear"
          trackingIdentifier="Clear"
        />
      )
    }, [disabled, size, onSearchClear, variant])

    return (
      <div
        className={classnames(
          styles['input-wrapper'],
          {
            [styles['disabled-input']]: disabled,
          },
          className
        )}
      >
        <label
          htmlFor={id}
          className={classnames(styles['label'], { [styles['visually-hidden']]: hiddenLabel })}
        >
          {label}
        </label>
        <div className={classnames(styles['input-container'], styles[size])}>
          <input
            disabled={disabled}
            id={id}
            className={inputClasses}
            ref={innerRef as React.Ref<HTMLInputElement>}
            {...rest}
          />
          {variant === 'live-search' && <SearchIcon className={styles['start-adornment']} />}
          {renderSearchButton}
          {renderClearButton}
        </div>
      </div>
    )
  }
)

export default SearchBar
